import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchBlogPosts, fetchBlogFilters } from '../../../Redux/Actions/actions';
import {
  BLOG_CATEGORIES,
  BLOG_SORTS,
  categoryLabel,
} from '../../../constants/blog';
import styles from './BlogFeed.module.css';

const allowedBlog = [
  'arosa@byraices.com',
  'tsanesteban@byraices.com',
  'dsanesteban@byraices.com',
  'jgarcia@byraices.com',
];

const PAGE_SIZE = 12;

const badgeLabels = {
  pdf: 'PDF',
  video: 'Video',
  image: 'Imagen',
  mixto: 'Multimedia',
  texto: 'Artículo',
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const BlogFeed = () => {
  const dispatch = useDispatch();
  const posts = useSelector((state) => state.blogPosts);
  const pagination = useSelector((state) => state.blogPagination);
  const backendFilters = useSelector((state) => state.blogFilters);
  const loading = useSelector((state) => state.blogLoading);
  const error = useSelector((state) => state.blogError);
  const user = useSelector((state) => state.user);

  const canManage = user?.user?.mail && allowedBlog.includes(user.user.mail);

  const [filters, setFilters] = useState({
    category: '',
    officeId: '',
    dateFrom: '',
    dateTo: '',
    search: '',
    sort: 'recientes',
  });
  const [page, setPage] = useState(1);

  const categories = backendFilters?.categories?.length
    ? backendFilters.categories
    : BLOG_CATEGORIES;
  const offices = backendFilters?.offices || [];
  const sorts = backendFilters?.sorts?.length ? backendFilters.sorts : BLOG_SORTS;

  useEffect(() => {
    dispatch(fetchBlogFilters());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      fetchBlogPosts({
        ...filters,
        status: 'publicado',
        page,
        limit: PAGE_SIZE,
      })
    );
  }, [dispatch, filters, page]);

  const handleFilterChange = (field, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({
      category: '',
      officeId: '',
      dateFrom: '',
      dateTo: '',
      search: '',
      sort: 'recientes',
    });
  };

  const totalPages = pagination?.totalPages || 1;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Feed institucional</h1>
          <p className={styles.subtitle}>Novedades, mercado, capacitaciones y más.</p>
        </div>
        {canManage && (
          <Link to="/blog/crear" className={styles.newBtn}>
            + Nueva publicación
          </Link>
        )}
      </div>

      <div className={styles.filters}>
        <div className={styles.filterField}>
          <label className={styles.label}>Buscar</label>
          <input
            type="text"
            className={styles.input}
            placeholder="Título o resumen…"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.label}>Categoría</label>
          <select
            className={styles.select}
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            <option value="">Todas</option>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        {offices.length > 0 && (
          <div className={styles.filterField}>
            <label className={styles.label}>Oficina</label>
            <select
              className={styles.select}
              value={filters.officeId}
              onChange={(e) => handleFilterChange('officeId', e.target.value)}
            >
              <option value="">Todas</option>
              {offices.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={styles.filterField}>
          <label className={styles.label}>Desde</label>
          <input
            type="date"
            className={styles.input}
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.label}>Hasta</label>
          <input
            type="date"
            className={styles.input}
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
          />
        </div>
        <div className={styles.filterField}>
          <label className={styles.label}>Ordenar</label>
          <select
            className={styles.select}
            value={filters.sort}
            onChange={(e) => handleFilterChange('sort', e.target.value)}
          >
            {sorts.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        <button type="button" className={styles.clearBtn} onClick={clearFilters}>
          Limpiar
        </button>
      </div>

      {loading && <p className={styles.info}>Cargando publicaciones…</p>}
      {error && !loading && <p className={styles.error}>{error}</p>}
      {!loading && !error && posts.length === 0 && (
        <p className={styles.info}>No hay publicaciones para los filtros seleccionados.</p>
      )}

      <div className={styles.grid}>
        {posts.map((post) => (
          <Link key={post.id} to={`/blog/${post.id}`} className={styles.card}>
            <div className={styles.cover}>
              {post.coverImage ? (
                <img
                  src={post.coverImage}
                  alt={post.title}
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                />
              ) : (
                <div className={styles.coverPlaceholder}>By Raíces</div>
              )}
              {post.primaryMediaType && (
                <span className={styles.badge}>
                  {badgeLabels[post.primaryMediaType] || post.primaryMediaType}
                </span>
              )}
              {post.featured && <span className={styles.featured}>Destacado</span>}
            </div>
            <div className={styles.cardBody}>
              <span className={styles.category}>
                {categoryLabel(post.category, categories)}
              </span>
              <h3 className={styles.cardTitle}>{post.title}</h3>
              {post.summary && <p className={styles.summary}>{post.summary}</p>}
              <div className={styles.cardFooter}>
                <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                {typeof post.views === 'number' && <span>{post.views} vistas</span>}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Anterior
          </button>
          <span className={styles.pageInfo}>
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default BlogFeed;
